import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import bindAll from 'lodash.bindall';
import {FormattedMessage} from 'react-intl';

import * as styles from './monitor.css';
import {List} from 'react-virtualized';
import DOMElementRenderer from '../../containers/dom-element-renderer.jsx';
import Cast from 'scratch-vm/src/util/cast';

class ListMonitorScroller extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'rowRenderer',
            'noRowsRenderer'
        ]);
    }
    noRowsRenderer () {
        return (
            <div className={classNames(styles.listRow, styles.listEmpty)}>
                <FormattedMessage
                    defaultMessage="(empty)"
                    description="Text shown on a list monitor when a list is empty"
                    id="gui.monitor.listMonitor.empty"
                />
            </div>
        );
    }
    rowRenderer ({index, key, style}) {
        /**
         * The implementation of array monitors was taken from AmpMod
         * https://codeberg.org/ampmod/ampmod/src/branch/develop/packages/gui/src/components/monitor/list-monitor-scroller.jsx
         */
        const rawValue = this.safeValues[index];
        const isObjEntry = rawValue && typeof rawValue === 'object' && rawValue.__isObjEntry;
        
        const valKey = isObjEntry ? rawValue.key : index;
        const value = isObjEntry ? rawValue.value : rawValue;

        const isNestedArray = Cast.isNormalArray(value);
        const isNestedObject = Cast.isNormalObject(value);
        return (
            <div
                className={styles.listRow}
                key={key}
                style={style}
            >
                <div className={styles.listIndex}>{isObjEntry ? valKey : index + 1 /* one indexed */}</div>
                <div
                    className={styles.listValue}
                    data-index={index}
                    style={{
                        background: this.props.categoryColor.background,
                        color: this.props.categoryColor.text,
                        cursor: (isNestedArray || isNestedObject) ? 'pointer' : 'default'
                    }}
                    // eslint-disable-next-line react/jsx-no-bind
                    onClick={() => {
                        if (isNestedArray || isNestedObject) {
                            this.props.onNavigateDown(valKey);
                        } else if (this.props.draggable) {
                            this.props.onActivate(index);
                        }
                    }}
                >
                    {this.props.draggable && this.props.activeIndex === index ? (
                        <div className={styles.inputWrapper}>
                            <input
                                autoFocus
                                autoComplete={false}
                                className={classNames(styles.listInput, 'no-drag')}
                                spellCheck={false}
                                style={{color: this.props.categoryColor.text}}
                                type="text"
                                value={String(
                                    (isNestedArray || isNestedObject || Cast.isCustomType(value)) &&
                                        typeof this.props.activeValue?.toListEditor === 'function' ?
                                        this.props.activeValue.toListEditor() :
                                        this.props.activeValue
                                )}
                                onBlur={this.props.onDeactivate}
                                onChange={this.props.onInput}
                                onFocus={this.props.onFocus}
                                onKeyDown={this.props.onKeyPress} // key down to get ahead of blur
                                readOnly={isNestedArray || isNestedObject || Cast.isCustomType(value)}
                            />
                            <div
                                className={styles.removeButton}
                                onMouseDown={this.props.onRemove} // mousedown to get ahead of blur
                            >
                                {'✖︎'}
                            </div>
                        </div>

                    ) : (
                        <div className={styles.valueInner}>
                            {isNestedArray ?
                                <i>{`Array(${value.length})`}</i> :
                                isNestedObject ?
                                    <i>{`Object(${value.size})`}</i> :
                                    Cast.isCustomType(value) && (
                                        typeof value?.toListItem === 'function' ||
                                        typeof value?.toMonitorContent === 'function'
                                    ) ?
                                        (<DOMElementRenderer
                                            domElement={typeof value?.toListItem === 'function' ?
                                                value.toListItem() :
                                                value.toMonitorContent()}
                                        />) :
                                        String(value)}
                        </div>
                    )}
                </div>
            </div>
        );
    }
    render () {
        const {height, values, width, activeIndex, activeValue} = this.props;
        const listHeight = Math.max(0, (Number.isFinite(height) ? height : 0) - 42 /* Header/footer size, approx */);
        const safeValues = values || [];
        const rowCount = safeValues.length;
        let scrollToIndex;
        // Keep the active index in view if defined, else must be undefined for List component
        if (
            (
                activeIndex !== null &&
                rowCount !== 0
            ) && (
                Number.isFinite(activeIndex) &&
                activeIndex >= 0 && activeIndex < rowCount
            )
        ) {
            scrollToIndex = Math.floor(activeIndex);
        }
        this.safeValues = safeValues;
        return (
            <List
                activeIndex={activeIndex}
                activeValue={activeValue}
                height={listHeight}
                noRowsRenderer={this.noRowsRenderer}
                rowCount={rowCount}
                rowHeight={24 /* Row size is same for all rows */}
                rowRenderer={this.rowRenderer}
                scrollToIndex={scrollToIndex} /* eslint-disable-line no-undefined */
                values={safeValues}
                width={width}
            />
        );
    }
}

ListMonitorScroller.propTypes = {
    activeIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    activeValue: PropTypes.string,
    categoryColor: PropTypes.shape({
        background: PropTypes.string.isRequired,
        text: PropTypes.string.isRequired
    }).isRequired,
    draggable: PropTypes.bool,
    height: PropTypes.number,
    onActivate: PropTypes.func,
    onDeactivate: PropTypes.func,
    onFocus: PropTypes.func,
    onInput: PropTypes.func,
    onKeyPress: PropTypes.func,
    onRemove: PropTypes.func,
    onNavigateDown: PropTypes.func,
    values: PropTypes.any,
    width: PropTypes.number
};
export default ListMonitorScroller;
